import { type ESize, Widgets } from '@common/enums';
import { FormStyleEnum, InputBorderEnum } from '@common/enums';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { FormInputSettingsFormType } from './sp-form-input-settings.type';
import type { FormInputSettingsFormGroupType } from './sp-form-input-settings.type';

import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';

import { InputBoolean } from '@common/helpers/convert';
import { CSS_RESTRICTIONS } from '@common/constants';

import { ERadioGroupType } from '@ui/sp-radio-group';
import { ButtonMode } from '@components/sp-custom-button';

import { ThemeStateService } from '@sites/site-theme/src/state';
import { ELoginEduView } from '@common/enums/login-edu-view-form.enum';
import type { Widget } from '@common/types/content.type';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { LoginEduWidgetData } from '@web/widgets/login-edu/type';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { WebLegacyWidgetEntity } from '@web/types';
import { NgIf } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SpColorPickerComponent } from '../../sp-color-picker/src/sp-color-picker.component';
import { SpInputNumComponent } from '../../sp-form-controls/sp-input-num/sp-input-num.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../../ui/sp-switch/src/sp-switch.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpRadioGroupComponent } from '../../../ui/sp-radio-group/src/sp-radio-group.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'sp-form-input-settings',
    templateUrl: './sp-form-input-settings.component.html',
    styleUrls: ['./sp-form-input-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        TooltipModule,
        SpColorPickerComponent,
        SpInputNumComponent,
        SpSwitchComponent,
        SpRadioGroupComponent,
        TranslateModule,
    ],
})
export class SpFormInputSettingsComponent {
    private readonly themeState = inject(ThemeStateService);

    @Input() public inputSettingsForm!: FormInputSettingsFormGroupType;
    @Input() @InputBoolean() public isPaymentForm: boolean = false;
    @Input() @InputBoolean() public isModalForm: boolean = false;
    @Input() @InputBoolean() public isTwoStepForm: boolean = false;
    @Input() @InputBoolean() public isLoginForm: boolean = false;
    @Input() @InputBoolean() public isCartForm: boolean = false;
    @Input() public widget: Widget;

    public readonly inputBorderEnum = InputBorderEnum;
    public readonly formStyleEnum = FormStyleEnum;
    public readonly radioGroupType = ERadioGroupType;
    public readonly buttonMode = ButtonMode;
    public readonly widgets = Widgets;

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public readonly defaultModalBackgroundColor = '#fbfcfc';

    public setTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.textColor.patchValue(presetName ?? value);
    }

    public setModalTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalTextColor.patchValue(presetName ?? value);
    }

    public setModalTitleTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalTitleTextColor.patchValue(presetName ?? value);
    }

    public setInputTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.inputTextColor.patchValue(presetName ?? value);
    }

    public setModalInputTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalInputTextColor.patchValue(presetName ?? value);
    }

    public setThemeBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setThemeModalInputBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalInputBackgroundColor.patchValue(presetName ?? value);
    }

    public setBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.borderColor.patchValue(presetName ?? value);
    }

    public setModalInputBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalInputBorderColor.patchValue(presetName ?? value);
    }

    public setLinkColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.linkColor.patchValue(presetName ?? value);
    }

    public setIsShowFieldsName(val: boolean): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.showLabels.patchValue(val);
    }

    public setIsModalShowFieldsName(val: boolean): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalShowLabels.patchValue(val);
    }

    public setInputSize(val: string): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.inputSize.patchValue(val as ESize);
    }

    public setModalInputSize(val: string): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalInputSize.patchValue(val as ESize);
    }

    public setInputBorderType(borderType: InputBorderEnum): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        if (borderType === this.inputBorderEnum.BOTTOM_BORDER) {
            this.borderRadius.patchValue(0, { emitEvent: false });
        }

        this.inputBorderType.patchValue(borderType);
    }

    public setModalInputBorderType(borderType: InputBorderEnum): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        if (borderType === this.inputBorderEnum.BOTTOM_BORDER) {
            this.modalBorderRadius.patchValue(0, { emitEvent: false });
        }

        this.modalInputBorderType.patchValue(borderType);
    }

    public setFormStyleType(formStyleType: FormStyleEnum): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.formStyleType.patchValue(formStyleType);
    }

    public setBorderRadius(value: number): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.borderRadius.patchValue(value);
    }

    public setModalInputBorderRadius(value: number): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalInputBorderRadius.patchValue(value);
    }

    public setModalBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalBackgroundColor.patchValue(presetName ?? value);
    }

    public setModalBorderRadius(value: number): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalBorderRadius.patchValue(value);
    }

    public setModalPadding(value: number): void {
        this.inputSettingsForm.markAsTouched({ onlySelf: true });

        this.modalPadding.patchValue(value);
    }

    public get isDisabled(): boolean {
        return this.inputBorderType.value === this.inputBorderEnum.BOTTOM_BORDER;
    }

    public get backgroundColor(): FormInputSettingsFormType['backgroundColor'] {
        return this.inputSettingsForm.get('backgroundColor') as FormInputSettingsFormType['backgroundColor'];
    }

    public get borderColor(): FormInputSettingsFormType['borderColor'] {
        return this.inputSettingsForm.get('borderColor') as FormInputSettingsFormType['borderColor'];
    }

    public get linkColor(): FormInputSettingsFormType['linkColor'] {
        return this.inputSettingsForm.get('linkColor') as FormInputSettingsFormType['linkColor'];
    }

    public get inputSize(): FormInputSettingsFormType['inputSize'] {
        return this.inputSettingsForm.get('inputSize') as FormInputSettingsFormType['inputSize'];
    }

    public get textColor(): FormInputSettingsFormType['textColor'] {
        return this.inputSettingsForm.get('textColor') as FormInputSettingsFormType['textColor'];
    }

    public get inputTextColor(): FormInputSettingsFormType['inputTextColor'] {
        return this.inputSettingsForm.get('inputTextColor') as FormInputSettingsFormType['inputTextColor'];
    }

    public get borderRadius(): FormInputSettingsFormType['borderRadius'] {
        return this.inputSettingsForm.get('borderRadius') as FormInputSettingsFormType['borderRadius'];
    }

    public get horizontalPosition(): FormInputSettingsFormType['horizontalPosition'] {
        return this.inputSettingsForm.get('horizontalPosition') as FormInputSettingsFormType['horizontalPosition'];
    }

    public get inputBorderType(): FormInputSettingsFormType['inputBorderType'] {
        return this.inputSettingsForm.get('inputBorderType') as FormInputSettingsFormType['inputBorderType'];
    }

    public get formStyleType(): FormInputSettingsFormType['formStyleType'] {
        return this.inputSettingsForm.get('formStyleType') as FormInputSettingsFormType['formStyleType'];
    }

    public get showLabels(): FormInputSettingsFormType['showLabels'] {
        return this.inputSettingsForm.get('showLabels') as FormInputSettingsFormType['showLabels'];
    }

    public get modalTitleTextColor(): FormInputSettingsFormType['modalTitleTextColor'] {
        return this.inputSettingsForm.get('modalTitleTextColor') as FormInputSettingsFormType['modalTitleTextColor'];
    }

    public get modalShowLabels(): FormInputSettingsFormType['modalShowLabels'] {
        return this.inputSettingsForm.get('modalShowLabels') as FormInputSettingsFormType['modalShowLabels'];
    }

    public get modalInputBorderType(): FormInputSettingsFormType['modalInputBorderType'] {
        return this.inputSettingsForm.get('modalInputBorderType') as FormInputSettingsFormType['modalInputBorderType'];
    }

    public get modalInputBackgroundColor(): FormInputSettingsFormType['modalInputBackgroundColor'] {
        return this.inputSettingsForm.get(
            'modalInputBackgroundColor'
        ) as FormInputSettingsFormType['modalInputBackgroundColor'];
    }

    public get modalInputBorderColor(): FormInputSettingsFormType['modalInputBorderColor'] {
        return this.inputSettingsForm.get(
            'modalInputBorderColor'
        ) as FormInputSettingsFormType['modalInputBorderColor'];
    }

    public get modalTextColor(): FormInputSettingsFormType['modalTextColor'] {
        return this.inputSettingsForm.get('modalTextColor') as FormInputSettingsFormType['modalTextColor'];
    }

    public get modalInputTextColor(): FormInputSettingsFormType['modalInputTextColor'] {
        return this.inputSettingsForm.get('modalInputTextColor') as FormInputSettingsFormType['modalInputTextColor'];
    }

    public get modalInputBorderRadius(): FormInputSettingsFormType['modalInputBorderRadius'] {
        return this.inputSettingsForm.get(
            'modalInputBorderRadius'
        ) as FormInputSettingsFormType['modalInputBorderRadius'];
    }

    public get modalInputSize(): FormInputSettingsFormType['modalInputSize'] {
        return this.inputSettingsForm.get('modalInputSize') as FormInputSettingsFormType['modalInputSize'];
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    public get PADDING_MIN(): number {
        return CSS_RESTRICTIONS.PADDING_MIN;
    }

    public get PADDING_MAX(): number {
        return CSS_RESTRICTIONS.PADDING_MAX;
    }

    public get modalBackgroundColor(): FormInputSettingsFormType['modalBackgroundColor'] {
        return this.inputSettingsForm.get('modalBackgroundColor') as FormInputSettingsFormType['modalBackgroundColor'];
    }

    public get modalBorderRadius(): FormInputSettingsFormType['modalBorderRadius'] {
        return this.inputSettingsForm.get('modalBorderRadius') as FormInputSettingsFormType['modalBorderRadius'];
    }

    public get modalPadding(): FormInputSettingsFormType['modalPadding'] {
        return this.inputSettingsForm.get('modalPadding') as FormInputSettingsFormType['modalPadding'];
    }

    public get isEDUModalWidget(): boolean {
        if (this.widget.name !== Widgets.LOGIN_EDU) return false;
        return (this.widget as WebLegacyWidgetEntity<LoginEduWidgetData>).data.formView === ELoginEduView.modal;
    }
}
